import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { UntypedFormControl } from '@angular/forms';
import { User } from '@domain/interface/user';
import { ACTIVE_USERS, ALL_ACCOUNT_USERS } from '@shared/utils/account-users';
import { AccessCacheService } from '@service/access-cache/access-cache.service';
import { UrlService } from '@service/url/url.service';
import { IS_NOT_EMPTY, Owner, RestService } from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';

@Component({
	selector: 'app-user-selector',
	templateUrl: './user-selector.component.html',
	styleUrls: ['./user-selector.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSelectorComponent implements OnInit {
	CURRENT_PAGE = 0;
	PAGE_SIZE = 50;
	accountId = null;

	@Output() userChanged: EventEmitter<any> = new EventEmitter();
	@Output() assignedToMeClicked: EventEmitter<any> = new EventEmitter();

	@Input() multiselect: boolean = false;
	@Input() singleselect: boolean = false;
	@Input() showAssignMe: boolean = false;
	@Input() selectedUsers = [];
	@Input() usersList: Array<Owner> = [];
	@Input() placeholder: string = 'Assign User';
	@Input() floatLabel: string = 'always';
	@Input() appearance: string = 'legacy';
	@Input() customCssClass: string = '';
	@Input() useSelect: boolean = true;
	@Input() useAutocomplete: boolean = false;
	@Input() isLoadingUsersList: boolean = false;
	@Input() customePanelClass: string = '';
	@Input() customAssignTexClass: string = '';
	@Input() assignText: string = 'Assign To Me';
	@Input() showInputWithIcon: boolean = false;
	// Label source only. Includes non-ACTIVE users so an existing assignment keeps its
	// name in the trigger even though it is not offered as an option.
	@Input() allUsersList: Array<Owner> = [];

	@ViewChild('userSelector') userSelector: MatSelect;

	userCtrl = new UntypedFormControl();
	filteredUsers = [];

	constructor(
		public utilService: UtilService,
		private urlService: UrlService,
		private restService: RestService,
		private cargochief: Cargochief,
		private cdRef: ChangeDetectorRef,
		private accessCacheService: AccessCacheService
	) {}

	ngOnInit() {
		this.accountId = this.cargochief.ACCOUNT_ID;
		if (this.useAutocomplete && this.selectedUsers.length > 0) {
			const user: Owner = this.usersList.filter((user) => user.userId == this.selectedUsers[0])[0];
			if (!this.utilService.isNullOrUndefined(user)) {
				this.userCtrl.setValue(user);
			}
		}
		if (this.utilService.isNullOrUndefined(this.usersList) || this.usersList.length == 0) {
			this.getUsersListForAccount();
		}

		if (this.useAutocomplete) {
			this.userCtrl.valueChanges.subscribe((val) => {
				this.filterOptions(val);
			});
		}
	}

	getSelectedLabel(): string {
		const labelUsers = this.allUsersList?.length ? this.allUsersList : this.usersList;
		return (this.selectedUsers || [])
			.map((userId) => labelUsers?.find((user) => user.userId == userId)?.name)
			.filter((name) => IS_NOT_EMPTY(name))
			.join(', ');
	}

	getUsersListForAccount() {
		this.usersList = null;
		this.isLoadingUsersList = true;
		const url = this.urlService.getActiveUsersByAccountIdUrl(
			this.accountId,
			this.CURRENT_PAGE,
			this.PAGE_SIZE
		);
		this.restService.GET(url).subscribe(
			(response) => {
				this.usersList = [];
				this.usersList = ACTIVE_USERS(response.data);
				// From the account cache, not this response: the endpoint is active-scoped and
				// paged, so it cannot supply labels for non-ACTIVE assignees.
				if (!this.allUsersList?.length) {
					this.allUsersList = ALL_ACCOUNT_USERS(this.accessCacheService.getAccountUsers());
				}
				this.isLoadingUsersList = false;
				this.cdRef.detectChanges();
			},
			(error) => {
				this.isLoadingUsersList = false;
			}
		);
	}

	changeOwner(user: Owner) {
		if (this.singleselect) {
			this.selectedUsers = [];
			this.usersList.map((userModel: Owner) => {
				if (userModel.userId != user.userId) {
					userModel.selected = false;
				}
			});
			if (user.selected) {
				this.selectedUsers.push(user.userId);
				this.userChanged.emit(user);
				if (!this.utilService.isNullOrUndefined(this.userSelector) && this.singleselect) {
					this.userSelector.close();
				}
			} else {
				this.userChanged.emit(null);
			}
		} else if (this.multiselect) {
			this.userChanged.emit(this.selectedUsers);
		}
	}

	setCurrentUser() {
		let user: Owner = this.usersList.filter((userModel: Owner) => {
			return userModel.userId == this.cargochief.USER_ID;
		})[0];
		if (!this.utilService.isNullOrUndefined(user)) {
			user.selected = true;
			this.selectedUsers = [];
			this.usersList.map((userModel: Owner) => {
				if (userModel.userId != user.userId) {
					userModel.selected = false;
				}
			});
			this.selectedUsers.push(user.userId);
			this.changeOwner(user);
			this.assignedToMeClicked.emit();
		}
	}

	filterOptions(text: string) {
		this.filteredUsers = this.usersList.filter(
			(user) => user.name.toLowerCase().indexOf(text.toString().toLowerCase()) === 0
		);
	}

	displayFn(user: Owner) {
		return user && user.name ? user.name : '';
	}

	selected(event: MatAutocompleteSelectedEvent): void {
		this.selectedUsers.push(event.option.value.userId);
		this.userChanged.emit(event.option.value);
	}

	initializeList() {
		if (this.utilService.isNullOrUndefined(this.userCtrl.value) && this.selectedUsers.length == 0) {
			this.userCtrl.setValue('');
		} else if (IS_NOT_EMPTY(this.userCtrl.value.name)) {
			this.filterOptions(this.userCtrl.value.name);
		}
	}
}
